import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useStudio } from "@/context/StudioContext";

// Top-level links. Some entries carry a `children` array to render a dropdown.
// `mobileOnly` keeps a link out of the desktop bar (to stop it crowding) while
// still exposing it in the hamburger menu.
const LINKS = [
  { label: "The Collection", to: "/collection" },
  { label: "Visualise", to: "/visualise" },
  { label: "The Archive", to: "/archive" },
  { label: "Journal", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact", mobileOnly: true },
  {
    label: "Architects & Designers",
    children: [
      { label: "Request Appointment", action: "booking" },
      { label: "Request a Sample", action: "sample" },
    ],
  },
];

const testId = (s) => `nav-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;

function DropdownDesktop({ item, onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  return (
    <div
      ref={ref}
      className="relative flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testId(item.label)}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className="link-underline text-[#D6D1CA] text-[11px] leading-none tracking-[0.12em] 2xl:tracking-[0.18em] uppercase hover:text-[#F5F4F0] relative inline-block pr-[18px] whitespace-nowrap"
        style={{ transition: "color 0.4s ease" }}
      >
        {item.label}
        <ChevronDown
          size={12}
          strokeWidth={1.6}
          className="absolute right-0 top-1/2 pointer-events-none"
          style={{
            // translateY must live in the inline style: an inline transform
            // overrides Tailwind's -translate-y-1/2, which left the arrow 6px low
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            transition: "transform 0.35s ease",
          }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="architects-designers-menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-3 min-w-[220px]"
          >
            <div className="bg-[#121315]/95 backdrop-blur-xl border border-[#8C7355]/25 rounded-sm overflow-hidden">
              {item.children.map((c) => (
                <button
                  key={c.label}
                  data-testid={testId(c.label)}
                  onClick={() => {
                    onAction(c.action);
                    setOpen(false);
                  }}
                  className="w-full text-left px-5 py-3.5 text-[11px] tracking-[0.18em] uppercase text-[#D6D1CA] hover:text-[#F5F4F0] hover:bg-[#8C7355]/12 border-b border-[#8C7355]/12 last:border-b-0"
                  style={{ transition: "all 0.3s ease" }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Nav() {
  const { openBooking, openSample } = useStudio();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const { pathname } = useLocation();
  const onSubPage = pathname !== "/";

  const doAction = (a) => {
    if (a === "booking") openBooking();
    if (a === "sample") openSample();
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backgroundColor: scrolled || onSubPage ? "rgba(18,19,21,0.82)" : "rgba(18,19,21,0)",
        backdropFilter: scrolled || onSubPage ? "blur(16px)" : "none",
        borderBottom: scrolled || onSubPage ? "1px solid rgba(140,115,85,0.18)" : "1px solid transparent",
        transition: "background-color 0.5s ease, border-color 0.5s ease, backdrop-filter 0.5s ease",
      }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between gap-8 h-20">
        <Link to="/" data-testid="logo" className="flex items-center gap-3 leading-none">
          <img
            src="/logo/logo-primary.png"
            alt="My Floor Studio"
            className="w-10 h-10 md:w-11 md:h-11 shrink-0"
          />
          <span className="font-serif-display text-[#F5F4F0] text-2xl leading-none tracking-tight whitespace-nowrap">My Floor Studio</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-8">
          {LINKS.filter((l) => !l.mobileOnly).map((l) =>
            l.children ? (
              <DropdownDesktop key={l.label} item={l} onAction={doAction} />
            ) : (
              <Link
                key={l.label}
                to={l.to}
                data-testid={testId(l.label)}
                className="link-underline text-[#D6D1CA] text-[11px] leading-none tracking-[0.12em] 2xl:tracking-[0.18em] uppercase hover:text-[#F5F4F0] inline-block whitespace-nowrap"
                style={{ transition: "color 0.4s ease" }}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <button
            data-testid="book-appointment-btn"
            onClick={openBooking}
            className="btn-bronze hidden sm:inline-flex items-center justify-center rounded-full px-5 2xl:px-6 h-10 text-[11px] leading-none tracking-[0.12em] 2xl:tracking-[0.18em] uppercase font-medium whitespace-nowrap"
          >
            Request Appointment
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenu((m) => !m)}
            className="xl:hidden text-[#F5F4F0]"
            aria-label="Toggle menu"
          >
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-[#121315]/95 backdrop-blur-xl border-t border-[#8C7355]/15"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) =>
                l.children ? (
                  <div key={l.label}>
                    <button
                      data-testid={`${testId(l.label)}-mobile`}
                      onClick={() => setMobileGroup((g) => (g === l.label ? null : l.label))}
                      className="w-full flex items-center justify-between text-[#D6D1CA] text-sm tracking-[0.15em] uppercase"
                    >
                      <span>{l.label}</span>
                      <ChevronDown
                        size={14}
                        style={{
                          transform: mobileGroup === l.label ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.35s ease",
                        }}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileGroup === l.label && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pl-4 mt-3 flex flex-col gap-3 border-l border-[#8C7355]/20">
                            {l.children.map((c) => (
                              <button
                                key={c.label}
                                data-testid={`${testId(c.label)}-mobile`}
                                onClick={() => {
                                  setMenu(false);
                                  setMobileGroup(null);
                                  doAction(c.action);
                                }}
                                className="text-left text-[#C9A876] text-xs tracking-[0.2em] uppercase py-3 min-h-[44px] flex items-center"
                              >
                                {c.label}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                    onClick={() => setMenu(false)}
                    className="text-[#D6D1CA] text-sm tracking-[0.15em] uppercase py-3 min-h-[44px] flex items-center"
                  >
                    {l.label}
                  </Link>
                )
              )}
              <button
                onClick={() => { setMenu(false); openBooking(); }}
                data-testid="mobile-book-appointment-btn"
                className="btn-bronze rounded-full px-6 py-3 text-[11px] tracking-[0.18em] uppercase mt-2"
              >
                Request Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
